import type { ApiError } from '../api-error';
export const APPLICATION_STATE_KINDS = [
  'loading',
  'empty',
  'no-results',
  'not-configured',
  'information-incomplete',
  'setup-incomplete',
  'partial',
  'unavailable',
  'access-denied',
  'not-found',
  'error',
] as const;

export type ApplicationStateKind = (typeof APPLICATION_STATE_KINDS)[number];

export type ApplicationStateTone =
  | 'neutral'
  | 'information'
  | 'warning'
  | 'danger'
  | 'unavailable';

type StateActionBase = Readonly<{
  label: string;
}>;

export type StateAction =
  | (StateActionBase & Readonly<{
      href: string;
      onClick?: never;
      disabled?: never;
    }>)
  | (StateActionBase & Readonly<{
      href?: never;
      onClick: () => void;
      disabled?: boolean;
    }>);

export type StateRetry = Readonly<{
  label?: string;
  onRetry: () => void;
  disabled?: boolean;
}>;

export type StateAdvancedDetail = Readonly<{
  summary: string;
  description?: string;
  items?: ReadonlyArray<Readonly<{
    label: string;
    value: string;
  }>>;
}>;

type ApplicationStateBase<Kind extends ApplicationStateKind> = Readonly<{
  kind: Kind;
  tone: ApplicationStateTone;
  heading: string;
  message: string;
  primaryAction?: StateAction;
  secondaryAction?: StateAction;
  retry?: StateRetry;
  supportReference?: string;
  lastKnownAt?: string;
  advancedDetail?: StateAdvancedDetail;
}>;

export type ApplicationState =
  | ApplicationStateBase<'loading'>
  | ApplicationStateBase<'empty'>
  | ApplicationStateBase<'no-results'>
  | ApplicationStateBase<'not-configured'>
  | ApplicationStateBase<'information-incomplete'>
  | ApplicationStateBase<'setup-incomplete'>
  | ApplicationStateBase<'partial'>
  | ApplicationStateBase<'unavailable'>
  | ApplicationStateBase<'access-denied'>
  | ApplicationStateBase<'not-found'>
  | ApplicationStateBase<'error'>;

export type StateLiveSemantics = Readonly<{
  role: 'status' | 'alert';
  live: 'polite' | 'assertive';
  busy: boolean;
}>;

/**
 * Marks copy that was deliberately written for staff, rather than copied from
 * a caught browser, provider, or server exception. Use this only for local
 * validation and recovery guidance whose wording is reviewed in source.
 */
export class ReviewedApplicationError extends Error {
  readonly reviewedForStaff = true;

  constructor(message: string) {
    super(message);
    this.name = 'ReviewedApplicationError';
  }
}

export function reviewedApplicationError(message: string): ReviewedApplicationError {
  return new ReviewedApplicationError(message);
}

export function isReviewedApplicationError(error: unknown): error is ReviewedApplicationError {
  return error instanceof ReviewedApplicationError;
}

const assertiveStateKinds = new Set<ApplicationStateKind>([
  'unavailable',
  'access-denied',
  'error',
]);

export function stateLiveSemantics(kind: ApplicationStateKind): StateLiveSemantics {
  if (assertiveStateKinds.has(kind)) {
    return { role: 'alert', live: 'assertive', busy: false };
  }
  return { role: 'status', live: 'polite', busy: kind === 'loading' };
}

/**
 * Compatibility callers historically pass arbitrary thrown values to ErrorState.
 * Do not inspect or copy that value into staff-visible output: it can contain
 * permissions, SQL, stack traces, provider responses, or internal routes.
 */
export function compatibilityFailureState(unsafeError: unknown): Extract<ApplicationState, { kind: 'error' }> {
  if (isReviewedApplicationError(unsafeError)) {
    return {
      kind: 'error',
      tone: 'danger',
      heading: 'Unable to complete this action',
      message: unsafeError.message,
    };
  }
  return {
    kind: 'error',
    tone: 'danger',
    heading: 'Unable to load this information',
    message: 'Try again. If the problem continues, contact support.',
  };
}

/**
 * Design item 3: a failed save keeps the person on the form. It says the changes were not saved, never offers to
 * navigate away (which would discard the form), and offers to copy the details for support.
 */
export function saveFailureState(error: Pick<ApiError, 'code' | 'message' | 'supportReference'>): ApplicationState {
  const message = error.code === 'access_denied'
    ? `You do not have permission to make this change. ${error.message}`
    : error.code === 'not_found'
      ? 'The record could not be found. It may have been moved or removed. Your entries are still here; reload the page to check.'
      : error.message;
  const details = [message, error.supportReference ? `Reference: ${error.supportReference}` : ''].filter(Boolean).join('\n');
  return {
    kind: 'error',
    tone: 'danger',
    heading: 'Your changes were not saved',
    message,
    secondaryAction: { label: 'Copy details', onClick: () => { void navigator.clipboard?.writeText(details).catch(() => undefined); } },
    ...(error.supportReference ? { supportReference: error.supportReference } : {}),
  };
}
